/**
 * Voertuigen / Materieel (F3) — gedeelde DTO's, Zod-schemas en label-maps.
 *
 * Bron: fs-pro `Vehicle` interface (`src/types/index.ts`) — gestripte/gemapte
 * variant naar Postgres-enums. Backoffice-only voor beheer; portal toont alleen
 * eigen toegewezen voertuig (read-only).
 */
import { z } from 'zod'
import type { VoertuigVerklaringStatus, VoertuigGebruikType } from './voertuig-verklaring'
// batch-37 onderhoudsrapport (FO-327) — embedded verwachting-samenvatting.
import type { OnderhoudVerwachtingSamenvattingDTO } from './voertuig-onderhoudsrapport'
// batch-59 voertuig-afspraken (FO-435 + FO-436) — de afgeleide actie-status, de
// afspraak-lijst en het recall-dossier landen in de bestaande voertuig-DTO's.
import type {
  VoertuigActieStatusDTO,
  VoertuigAfspraakDTO,
  VoertuigTerugroepDossierDTO,
} from './voertuig-afspraken'
// batch-65 apk-rapport (FO-478) — APK-rapporten op de logregel en het voertuig.
import type { ApkKeuringUitslag, VoertuigApkRapportDTO } from './apk-rapport'
// batch-68 werkplaatsfacturen (FO-341) - onderhoudsbadge (lijst, detail, portal).
import type {
  VoertuigOnderhoudBadgeDTO,
  VoertuigOnderhoudFilter,
  VoertuigOnderhoudLijstBadgeDTO,
} from './werkplaatsfacturen'

// ─── Enums ──────────────────────────────────────────────────────────────────

export const VOERTUIG_TYPE = [
  'PERSONENAUTO',
  'BESTELBUS',
  'VRACHTWAGEN',
  'AANHANGER',
  'HEFTRUCK',
  'HOOGWERKER',
  'OVERIG',
] as const
export type VoertuigType = (typeof VOERTUIG_TYPE)[number]

export const VOERTUIG_BRANDSTOF = [
  'BENZINE',
  'DIESEL',
  'ELEKTRISCH',
  'HYBRIDE',
  'HYBRIDE_BENZINE',
  'HYBRIDE_DIESEL',
  'LPG',
  'WATERSTOF',
  'OVERIG',
] as const
export type VoertuigBrandstof = (typeof VOERTUIG_BRANDSTOF)[number]

export const VOERTUIG_STATUS = [
  'ACTIEF',
  'ONDERHOUD',
  'BESCHIKBAAR',
  'AFGESCHREVEN',
  'VERKOCHT',
  // FO-038 (batch-16) — defect/niet-inzetbaar, los van ONDERHOUD (gepland).
  'KAPOT',
] as const
export type VoertuigStatus = (typeof VOERTUIG_STATUS)[number]

/**
 * FO-038 (batch-16) — statussen waarop een voertuig NIET inzetbaar/koppelbaar
 * is. KAPOT (defect) komt bovenop de bestaande niet-inzetbare statussen.
 * Eén bron voor de koppel-view, de toewijs-guard en beschikbaarheidschecks.
 */
export const VOERTUIG_NIET_INZETBAAR_STATUS = [
  'KAPOT',
  'AFGESCHREVEN',
  'VERKOCHT',
] as const satisfies readonly VoertuigStatus[]

/** Voertuig is inzetbaar/koppelbaar (niet KAPOT/AFGESCHREVEN/VERKOCHT). */
export function isVoertuigInzetbaar(status: VoertuigStatus): boolean {
  return !(VOERTUIG_NIET_INZETBAAR_STATUS as readonly VoertuigStatus[]).includes(status)
}

export const VOERTUIG_EIGENDOM = [
  'KOOP',
  'FINANCIAL_LEASE',
  'OPERATIONAL_LEASE',
  'HUUR',
  'LEEN',
] as const
export type VoertuigEigendom = (typeof VOERTUIG_EIGENDOM)[number]

export const VOERTUIG_TOEWIJZING_SOORT = ['VAST', 'TIJDELIJK'] as const
export type VoertuigToewijzingSoort = (typeof VOERTUIG_TOEWIJZING_SOORT)[number]

/**
 * FO-040 (batch-16) — reden waarom een toewijzing als dubbele-bestuurder-overlap
 * is gemarkeerd. Spiegelt de pure detectie in @streep/utils
 * (`ToewijzingOverlapReden`).
 */
export const TOEWIJZING_OVERLAP_REDEN = ['MEERDERE_VOERTUIGEN', 'MEERDERE_BESTUURDERS'] as const
export type ToewijzingOverlapReden = (typeof TOEWIJZING_OVERLAP_REDEN)[number]

export const TOEWIJZING_OVERLAP_REDEN_LABELS: Record<ToewijzingOverlapReden, string> = {
  MEERDERE_VOERTUIGEN:  'Bestuurder op meerdere voertuigen',
  MEERDERE_BESTUURDERS: 'Voertuig met meerdere bestuurders',
}

export const VOERTUIG_ONDERHOUD_TYPE = [
  'APK',
  'ONDERHOUDSBEURT',
  'REPARATIE',
  'BANDEN',
  // batch-38 garagefacturen (FO-329) — schadeherstel door de garage.
  'SCHADE',
  'OVERIG',
] as const
export type VoertuigOnderhoudType = (typeof VOERTUIG_ONDERHOUD_TYPE)[number]

export const VOERTUIG_AUDIT_ACTIE = [
  'AANGEMAAKT',
  'BIJGEWERKT',
  'TOEGEWEZEN',
  'INGETROKKEN',
  'ONDERHOUD_GEPLAND',
  'ONDERHOUD_AFGEROND',
  'STATUS_GEWIJZIGD',
  'VERWIJDERD',
  // FO-113 (batch-22) — zakelijk/privé-gebruik-verklaring.
  'VERKLARING_VASTGELEGD',
  'VERKLARING_INGETROKKEN',
] as const
export type VoertuigAuditActie = (typeof VOERTUIG_AUDIT_ACTIE)[number]

// ─── Labels ─────────────────────────────────────────────────────────────────

export const VOERTUIG_TYPE_LABELS: Record<VoertuigType, string> = {
  PERSONENAUTO: 'Personenauto',
  BESTELBUS:    'Bestelbus',
  VRACHTWAGEN:  'Vrachtwagen',
  AANHANGER:    'Aanhanger',
  HEFTRUCK:     'Heftruck',
  HOOGWERKER:   'Hoogwerker',
  OVERIG:       'Overig',
}

export const VOERTUIG_BRANDSTOF_LABELS: Record<VoertuigBrandstof, string> = {
  BENZINE:         'Benzine',
  DIESEL:          'Diesel',
  ELEKTRISCH:      'Elektrisch',
  HYBRIDE:         'Hybride',
  HYBRIDE_BENZINE: 'Hybride (benzine)',
  HYBRIDE_DIESEL:  'Hybride (diesel)',
  LPG:             'LPG',
  WATERSTOF:       'Waterstof',
  OVERIG:          'Overig',
}

export const VOERTUIG_STATUS_LABELS: Record<VoertuigStatus, string> = {
  ACTIEF:       'Actief',
  ONDERHOUD:    'In onderhoud',
  BESCHIKBAAR:  'Beschikbaar',
  AFGESCHREVEN: 'Afgeschreven',
  VERKOCHT:     'Verkocht',
  KAPOT:        'Kapot',
}

export const VOERTUIG_EIGENDOM_LABELS: Record<VoertuigEigendom, string> = {
  KOOP:              'Koop',
  FINANCIAL_LEASE:   'Financial Lease',
  OPERATIONAL_LEASE: 'Operational Lease',
  HUUR:              'Huur',
  LEEN:              'Leen',
}

export const VOERTUIG_TOEWIJZING_SOORT_LABELS: Record<VoertuigToewijzingSoort, string> = {
  VAST:      'Vast',
  TIJDELIJK: 'Tijdelijk',
}

export const VOERTUIG_ONDERHOUD_TYPE_LABELS: Record<VoertuigOnderhoudType, string> = {
  APK:             'APK-keuring',
  ONDERHOUDSBEURT: 'Onderhoudsbeurt',
  REPARATIE:       'Reparatie',
  BANDEN:          'Banden',
  SCHADE:          'Schadeherstel',
  OVERIG:          'Overig',
}

export const VOERTUIG_AUDIT_ACTIE_LABELS: Record<VoertuigAuditActie, string> = {
  AANGEMAAKT:         'Aangemaakt',
  BIJGEWERKT:         'Bijgewerkt',
  TOEGEWEZEN:         'Toegewezen',
  INGETROKKEN:        'Ingetrokken',
  ONDERHOUD_GEPLAND:  'Onderhoud gepland',
  ONDERHOUD_AFGEROND: 'Onderhoud afgerond',
  STATUS_GEWIJZIGD:   'Status gewijzigd',
  VERWIJDERD:         'Verwijderd',
  VERKLARING_VASTGELEGD:  'Verklaring vastgelegd',
  VERKLARING_INGETROKKEN: 'Verklaring ingetrokken',
}

// Status badge variant (consumed by UI Badge component)
export const VOERTUIG_STATUS_BADGE: Record<
  VoertuigStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'
> = {
  ACTIEF:       'success',
  ONDERHOUD:    'warning',
  BESCHIKBAAR:  'info',
  AFGESCHREVEN: 'secondary',
  VERKOCHT:     'secondary',
  KAPOT:        'destructive',
}

// ─── Validators ─────────────────────────────────────────────────────────────

/**
 * NL-kentekenpatroon (sidecodes 1-13). Geen exhaustieve check op alle codes,
 * maar accepteert hoofdletters + cijfers + max 1 streepje. Voldoende voor
 * input-validatie; RDW is single source of truth.
 */
const KENTEKEN_RE = /^[A-Z0-9]{1,3}-?[A-Z0-9]{1,3}-?[A-Z0-9]{1,3}$/

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet YYYY-MM-DD zijn')

// LET OP (18-08): deze transforms moeten `undefined` BEWAREN. Een transform die
// van "veld niet meegestuurd" een expliciete `null` maakt, materialiseert bij
// een partial PATCH álle optionele velden als null - en dan wist een
// gerichte update (bv. het groene-kaart-blok dat 2 velden stuurt) stilletjes
// de ~30 overige voertuigvelden (APK-datum, kleur, lease, ...). Semantiek:
// afwezig = niet wijzigen; expliciet '' of null = veld wissen.
const optionalIsoDate = z
  .union([
    isoDate,
    z.literal(''),
    z.null(),
  ])
  .optional()
  .transform((v) => (v === undefined ? undefined : v === '' || v === null ? null : v))

const optionalString = z
  .string()
  .trim()
  .max(500)
  .optional()
  .nullable()
  .transform((v) => (v === undefined ? undefined : v === null || v.trim() === '' ? null : v))

// batch-62 garage-voertuig - beschrijving van een onderhoudsregel: zelfde limiet als
// document toepassen en afronden (2000), zodat een ingelezen tekst na bewerken weer
// op te slaan is (de DB-kolom is text).
const optionalBeschrijving = z
  .string()
  .trim()
  .max(2000, 'Maximaal 2000 tekens')
  .optional()
  .nullable()
  .transform((v) => (v === undefined ? undefined : v === null || v.trim() === '' ? null : v))

// batch-38 schade-pdf (FO-330) — mailadres-veld: formaat gevalideerd wanneer
// gevuld; leeg/'' normaliseert naar null (read-DTO null blijft accepteerbaar).
const optionalEmail = z
  .union([z.string().trim().email('Ongeldig mailadres').max(200), z.literal(''), z.null()])
  .optional()
  .transform((v) => (v === undefined ? undefined : v === '' || v === null ? null : v))

// j/n-keuze met default "onbekend" (null) — voor de verzekering-blokken.
const optionalBoolean = z.boolean().nullable().optional()

// ─── Zod-schemas ────────────────────────────────────────────────────────────

export const createVoertuigSchema = z.object({
  kenteken:  z.string().trim().toUpperCase().regex(KENTEKEN_RE, 'Ongeldig kenteken-formaat'),
  type:      z.enum(VOERTUIG_TYPE),
  brandstof: z.enum(VOERTUIG_BRANDSTOF),
  status:    z.enum(VOERTUIG_STATUS).optional(),
  eigendom:  z.enum(VOERTUIG_EIGENDOM).optional(),

  merk:           z.string().trim().min(1).max(80),
  model:          z.string().trim().min(1).max(80),
  bouwjaar:       z.number().int().min(1900).max(2100).nullable().optional(),
  kleur:          optionalString,
  chassisNummer:  optionalString,
  transmissie:    optionalString,
  kilometerstand: z.number().int().min(0).max(2_000_000).nullable().optional(),

  apkVervaldatum:         optionalIsoDate,
  verzekeringVervaldatum: optionalIsoDate,
  verzekeraar:            optionalString,
  verzekeringPolisNummer: optionalString,
  verzekeringspakket:     optionalString,

  // batch-37/38 schadeformulier (FO-326/FO-330) — het voertuig is de bron van
  // ALLE verzekeraarsgegevens voor de schademelding (vak 8 + blad 2 + de
  // conceptmail-ontvanger). Sectie Verzekering op het voertuig-detail.
  groeneKaartNummer:      optionalString,
  groeneKaartGeldigVanaf: optionalIsoDate,
  groeneKaartGeldigTot:   optionalIsoDate,
  verzekeringAgentschap:  optionalString, // agentschap/tussenpersoon (naam)
  verzekeringAllRisks:    optionalBoolean,
  verzekeraarEmail:       optionalEmail, // ontvanger schade-conceptmail
  rechtsbijstandVerzekering:  optionalBoolean,
  rechtsbijstandPolisNummer:  optionalString,
  rechtsbijstandMaatschappij: optionalString,
  inzittendenVerzekering:     optionalBoolean,
  inzittendenPolisNummer:     optionalString,
  inzittendenMaatschappij:    optionalString,

  laatsteOnderhoudDatum:   optionalIsoDate,
  volgendeOnderhoudKm:     z.number().int().min(0).nullable().optional(),
  garageNaam:              optionalString,
  onderhoudBij:            optionalString,
  locatieGarage:           optionalString,
  laatsteOnderhoudsmoment: optionalIsoDate,
  onderhoudshistorie:      optionalString,

  leaseMaatschappij:  optionalString,
  contractNummer:     optionalString,
  contractStartDatum: optionalIsoDate,
  contractEindDatum:  optionalIsoDate,
  looptijdMaanden:    z.number().int().min(0).max(600).nullable().optional(),
  maandbedragExclBtw: z.number().min(0).max(100_000).nullable().optional(),
  maandbedragInclBtw: z.number().min(0).max(100_000).nullable().optional(),

  manierVanAankoop:        optionalString,
  gekochtBijGarage:        optionalString,

  tankpasNummer: optionalString,
  // FO-444 (spec 144) - NMBRS-voertuigreferentie (alternatieve matchsleutel
  // voor de loonstrook-voertuigcontrole).
  nmbrsReferentie: optionalString,

  aanschafDatum:   optionalIsoDate,
  aanschafBedrag:  z.number().min(0).nullable().optional(),
  cataloguswaarde: z.number().min(0).nullable().optional(),
  // FO-361 (batch-41) - dagwaarde (waarde economisch verkeer): grondslag voor
  // de youngtimer-bijtelling (> 15 jaar). Handmatig in te vullen.
  dagwaarde: z.number().min(0).nullable().optional(),
  // Spec 40 - datum eerste toelating (DET): voedt samen met cataloguswaarde +
  // brandstof de bijtelling-calculator. Uit RDW of handmatig in te vullen.
  datumEersteToelating: optionalIsoDate,

  // FO-038 (batch-16, punt 3) — bijtelling is GEEN bewerkbaar voertuig-veld meer:
  // het is één bron op de medewerker (Employee.bijtelling, Facilities-blok). De
  // legacy-kolommen op Voertuig blijven bestaan (geen waarde verliezen) maar zijn
  // niet langer via create/update beschrijfbaar; het voertuig toont de bijtelling
  // hooguit afgeleid van de huidige bestuurder.
  kentekenKleur:        optionalString,

  // Borg
  borgBetaald: z.boolean().nullable().optional(),
  borgBedrag:  z.number().min(0).max(100_000).nullable().optional(),

  // Documenten
  kentekenBewijsAanwezig:   z.boolean().nullable().optional(),
  aankoopBewijsAanwezig:    z.boolean().nullable().optional(),
  schemaEnContractAanwezig: z.boolean().nullable().optional(),

  notities: optionalString,

  // batch-46 (FO-394) — herkomst: de veldnamen die bij aanmaken daadwerkelijk uit
  // RDW-autofill kwamen (create-form). De route filtert deze op de RDW-beheerde set
  // en persisteert 'm als `Voertuig.bronRdw`, zodat de sync alleen die velden bijhoudt
  // en de "Opgehaald bij RDW"-badge de echte herkomst toont. Alleen bij CREATE; de
  // PATCH-route negeert een meegestuurde `bronRdw` (server-beheerd via carve-out).
  bronRdw: z.array(z.string()).optional(),

  // batch-70 voertuig-meldingen (FO-527 §B4) - de leasemaatschappij als relatie
  // (Client type LEVERANCIER; de route controleert het type). Null = ontkoppelen.
  leaseMaatschappijId: z.string().min(1).nullable().optional(),
})
export type CreateVoertuigInput = z.infer<typeof createVoertuigSchema>

export const updateVoertuigSchema = createVoertuigSchema.partial()
export type UpdateVoertuigInput = z.infer<typeof updateVoertuigSchema>

export const toewijzingSchema = z.object({
  employeeId: z.string().min(1),
  startDatum: optionalIsoDate,
  notities:   optionalString,
})
export type ToewijzingInput = z.infer<typeof toewijzingSchema>

export const voertuigIntrekkenSchema = z.object({
  eindDatum: optionalIsoDate,
  notities:  optionalString,
})
export type VoertuigIntrekkenInput = z.infer<typeof voertuigIntrekkenSchema>

export const onderhoudSchema = z.object({
  type:           z.enum(VOERTUIG_ONDERHOUD_TYPE),
  uitvoerDatum:   isoDate,
  kilometerstand: z.number().int().min(0).max(2_000_000).nullable().optional(),
  garage:         optionalString,
  beschrijving:   optionalBeschrijving,
  // FO-320 — handmatige kosten (voor onderhoud buiten het factuursysteem). Bij
  // een gekoppelde inkoopfactuurregel is de factuur de waarheid en wordt dit veld
  // genegeerd (read-only in de UI).
  kosten:               z.number().min(0).nullable().optional(),
  inkoopFactuurRegelId: z.string().min(1).nullable().optional(),
})
export type OnderhoudInput = z.infer<typeof onderhoudSchema>

/**
 * FO-329 (batch-38) — logregel corrigeren (PATCH /voertuigen/:id/onderhoud/:onderhoudId).
 * Alle velden optioneel (partial); de factuur-koppelingen zelf wijzig je niet
 * hier (factuur = waarheid; kosten wordt bij een koppeling server-side genegeerd).
 */
export const onderhoudUpdateSchema = z.object({
  type:           z.enum(VOERTUIG_ONDERHOUD_TYPE).optional(),
  uitvoerDatum:   isoDate.optional(),
  kilometerstand: z.number().int().min(0).max(2_000_000).nullable().optional(),
  garage:         optionalString,
  beschrijving:   optionalBeschrijving,
  kosten:         z.number().min(0).nullable().optional(),
  // batch-62 garage-voertuig (FO-462) - kostencategorie van een regel ZONDER
  // factuurregels (handmatig/import). Herclassificeren vereist een reden
  // (server-side afgedwongen: alleen bij een wijziging van een gezette waarde).
  kostenCategorie: z.enum(['ONDERHOUD', 'SCHADE_REPARATIE']).nullable().optional(),
  correctieReden:  z.string().trim().max(1000).nullable().optional(),
})
export type OnderhoudUpdateInput = z.infer<typeof onderhoudUpdateSchema>

/**
 * Voertuig wisselen in één handeling (batch-6 voertuig-stream).
 * Gedeeld tussen API (`POST /voertuigen/wissel`) en de backoffice-wissel-modal.
 *
 * - `soort=VAST`: beëindigt de actieve VAST-toewijzing van de medewerker en
 *   maakt een nieuwe VAST-toewijzing op `nieuwVoertuigId`.
 * - `soort=TIJDELIJK`: laat de vaste toewijzing staan (zet
 *   `vervangtToewijzingId`); een eerdere actieve TIJDELIJK wordt beëindigd.
 * - `forceer`: vereist wanneer het nieuwe voertuig bij iemand anders zit —
 *   zonder forceer antwoordt de API 409 met de naam van de huidige bestuurder.
 */
export const wisselVoertuigSchema = z
  .object({
    employeeId:         z.string().min(1),
    nieuwVoertuigId:    z.string().min(1),
    soort:              z.enum(VOERTUIG_TOEWIJZING_SOORT),
    verwachteEindDatum: optionalIsoDate,
    notitie:            optionalString,
    forceer:            z.boolean().optional(),
  })
  .refine((v) => v.soort === 'TIJDELIJK' || v.verwachteEindDatum == null, {
    path: ['verwachteEindDatum'],
    message: 'Verwachte einddatum is alleen van toepassing op een tijdelijke toewijzing',
  })
export type WisselVoertuigInput = z.infer<typeof wisselVoertuigSchema>

export const beeindigTijdelijkSchema = z.object({
  eindDatum: optionalIsoDate,
  notitie:   optionalString,
})
export type BeeindigTijdelijkInput = z.infer<typeof beeindigTijdelijkSchema>

/**
 * Mini-form "Leen- of huurauto toewijzen": maakt in één keer een minimaal
 * Voertuig aan (eigendom LEEN/HUUR, status ACTIEF) en wijst het direct als
 * TIJDELIJK toe. Bestaat het kenteken al, dan koppelt de UI dat voertuig
 * i.p.v. een duplicaat te maken.
 */
export const leenVoertuigSchema = z.object({
  kenteken: z.string().trim().toUpperCase().regex(KENTEKEN_RE, 'Ongeldig kenteken-formaat'),
  merk:     z.string().trim().min(1).max(80),
  model:    z.string().trim().min(1).max(80),
  eigendom: z.enum(['LEEN', 'HUUR']),
})
export type LeenVoertuigInput = z.infer<typeof leenVoertuigSchema>

export const listToewijzingenQuerySchema = z.object({
  bvId:       z.string().optional(),
  soort:      z.enum(VOERTUIG_TOEWIJZING_SOORT).optional(),
  employeeId: z.string().optional(),
  zoek:       z.string().optional(),
})
export type ListToewijzingenQuery = z.infer<typeof listToewijzingenQuerySchema>

/**
 * Toewijzingshistorie over het hele wagenpark (`GET /voertuigen/toewijzingen/historie`):
 * ook beeindigde toewijzingen en afgevoerde voertuigen, zodat je per medewerker
 * terugziet welke auto('s) iemand wanneer had. `zoek` matcht per woord op voor- of
 * achternaam, kenteken, merk of model ("jan jansen" vindt Jan Jansen).
 */
export const toewijzingHistorieQuerySchema = z.object({
  zoek:     z.string().trim().max(100).optional(),
  volgorde: z.enum(['nieuwste', 'oudste']).default('nieuwste'),
  page:     z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type ToewijzingHistorieQuery = z.infer<typeof toewijzingHistorieQuerySchema>

/** Eén rij uit de toewijzingshistorie (`GET /voertuigen/toewijzingen/historie`). */
export interface ToewijzingHistorieRowDTO {
  toewijzingId:       string
  employeeId:         string
  employeeName:       string
  voertuigId:         string
  kenteken:           string
  merk:               string
  model:              string
  /** Het voertuig is inmiddels verwijderd/afgevoerd (geen detailpagina meer). */
  voertuigVerwijderd: boolean
  soort:              VoertuigToewijzingSoort
  startDatum:         string
  /** `null` = de toewijzing loopt nog. */
  eindDatum:          string | null
  verwachteEindDatum: string | null
  notities:           string | null
}

export const listVoertuigQuerySchema = z.object({
  status:        z.enum(VOERTUIG_STATUS).optional(),
  type:          z.enum(VOERTUIG_TYPE).optional(),
  toegewezenAan: z.string().optional(), // employeeId; "geen" = unassigned
  zoek:          z.string().optional(),
  // Filter: alleen voertuigen met >=1 openstaande RDW-terugroepactie (recall).
  // String-enum i.p.v. z.coerce.boolean() (die maakt "false" -> true).
  metTerugroepactie: z.enum(['true', 'false']).optional(),
  // batch-37 onderhoudsrapport (FO-327) — filter op de actuele onderhoud-
  // verwachting-toestand. 'AANDACHT' = achterstallig + onderhoud-nu + binnenkort.
  verwachting: z
    .enum(['GEPLAND', 'BINNENKORT', 'ACHTERSTALLIG', 'ONDERHOUD_NU', 'AANDACHT'])
    .optional(),
  // batch-38 schade-pdf (FO-330) — filter "verzekering incompleet": de
  // aanvul-werkvoorraad voor de schade-flow (zie isVerzekeringIncompleet).
  verzekeringIncompleet: z.enum(['true', 'false']).optional(),
  // batch-59 voertuig-afspraken (FO-435 stap 4) - filter op de afgeleide
  // actie-status: ACTIE_VEREIST (vervalt binnenkort zonder afspraak) of GEPLAND
  // (er staat een afspraak). Afgeleid, dus de route bepaalt eerst de matchende
  // ids en filtert daar in SQL op (paginering blijft server-side kloppen).
  actieStatus: z.enum(['ACTIE_VEREIST', 'GEPLAND']).optional(),
  // batch-68 materieel-exports (FO-340 §3.6) — eigendomsvorm en eigenaar-BV;
  // dezelfde filters gelden voor de lijst en de voertuigen-export (gedeelde
  // where-builder `services/voertuigen/list-where.ts`).
  eigendom:      z.enum(VOERTUIG_EIGENDOM).optional(),
  eigenaarBvId:  z.string().min(1).optional(),
  // batch-68 werkplaatsfacturen (FO-341 §3.4) - filter op de berekende
  // onderhoudsstatus (reminders, ook zonder telematica). AANDACHT = binnenkort +
  // achterstallig. Afgeleid: de route bepaalt de ids en filtert daar in SQL op.
  onderhoud: z.enum(['BINNENKORT', 'ACHTERSTALLIG', 'AANDACHT'] satisfies readonly VoertuigOnderhoudFilter[]).optional(),
  page:          z.coerce.number().int().min(1).default(1),
  pageSize:      z.coerce.number().int().min(1).max(200).default(50),
})
export type ListVoertuigQuery = z.infer<typeof listVoertuigQuerySchema>

// ─── DTO's ──────────────────────────────────────────────────────────────────

/**
 * Eén RDW-terugroepactie (recall) op een voertuig, zoals getoond in de UI.
 * Gevuld vanuit de gedenormaliseerde VoertuigTerugroepactie-tabel (RDW-cache).
 */
export interface VoertuigTerugroepactieDTO {
  id:                  string
  /** RDW-referentiecode (bv. "MGP230312"). */
  referentiecodeRdw:   string
  openstaand:          boolean
  /** Leesbare RDW-status ("Openstaande terugroepactie"). */
  statusOmschrijving:  string
  omschrijvingDefect:  string | null
  categorieDefect:     string | null
  materieleGevolgen:   string | null
  herstelOmschrijving: string | null
  /** Rauwe RDW-risicocode (GEM/ERN/...). UI mapt via RDW_RISICO_LABELS. */
  risicobeoordeling:   string | null
  meldendeProducent:   string | null
  meerInformatieTel:   string | null
  meerInformatieUrl:   string | null
  /** Publicatiedatum RDW (ISO yyyy-MM-dd) of null. */
  publicatiedatumRdw:  string | null
}

/** RDW-risicocodes → leesbaar label. UI valt terug op de rauwe code. */
export const RDW_RISICO_LABELS: Record<string, string> = {
  ERN: 'Ernstig',
  GEM: 'Gemiddeld',
  LAA: 'Laag',
  HOO: 'Hoog',
}

export interface VoertuigToewijzingDTO {
  id:                   string
  voertuigId:           string
  employeeId:           string
  employeeName:         string
  startDatum:           string
  eindDatum:            string | null
  notities:             string | null
  soort:                VoertuigToewijzingSoort
  verwachteEindDatum:   string | null
  vervangtToewijzingId: string | null
  createdAt:            string
}

/** Rij in het bestuurders-overzicht (`GET /voertuigen/toewijzingen`). */
export interface BestuurderToewijzingRowDTO {
  toewijzingId:         string
  employeeId:           string
  employeeName:         string
  voertuigId:           string
  kenteken:             string
  merk:                 string
  model:                string
  soort:                VoertuigToewijzingSoort
  startDatum:           string
  verwachteEindDatum:   string | null
  /** Gebruikende BV (toewijzing) ?? eigenaar-BV (voertuig). */
  bvId:                 string | null
  bvNaam:               string | null
  /** Gevuld bij TIJDELIJK: het kenteken van de vaste auto die vervangen wordt. */
  vervangtKenteken:     string | null
  vervangtToewijzingId: string | null
  /** FO-040 (batch-16) — dubbele-bestuurder-overlap (leeg = geen overlap). */
  overlapRedenen:       ToewijzingOverlapReden[]
  /**
   * FO-113 (batch-22) — verklaring-status zakelijk/privé. `null` = niet van
   * toepassing (TIJDELIJK-toewijzing zonder verklaring). Voor VAST altijd gevuld.
   */
  verklaring:           VoertuigVerklaringStatus | null
  /** Datum (ISO) van de laatste verklaring, voor tooltip/sortering. */
  verklaringDatum:      string | null
}

/** Actieve medewerker zonder voertuigtoewijzing (`GET /voertuigen/zonder-voertuig`). */
export interface MedewerkerZonderVoertuigDTO {
  employeeId:     string
  employeeName:   string
  employeeNumber: string
  bvId:           string | null
  bvNaam:         string | null
}

/** Resultaat van `POST /voertuigen/wissel`. */
export interface WisselVoertuigResultDTO {
  toewijzingId:           string
  voertuigId:             string
  employeeId:             string
  soort:                  VoertuigToewijzingSoort
  /** Beëindigde eigen toewijzing van de medewerker (zelfde soort), indien aanwezig. */
  beeindigdeToewijzingId: string | null
  /** Geforceerd beëindigde toewijzing van de vorige bestuurder van het nieuwe voertuig. */
  vrijgemaakteToewijzingId: string | null
  vervangtToewijzingId:   string | null
}

/** FO-329 — één gekoppelde inkoopfactuur op een onderhoudslog-regel. */
export interface VoertuigOnderhoudFactuurKoppelingDTO {
  factuurId: string
  factuurNr: string
  /** BRON | DEELFACTUUR | CREDITNOTA (string-union uit garage-onderhoud.ts). */
  rol: 'BRON' | 'DEELFACTUUR' | 'CREDITNOTA'
  /** Voertuig-aandeel van deze factuur (som VOERTUIG/DEFINITIEF-toewijzingen). */
  bedrag: number
}

export interface VoertuigOnderhoudDTO {
  id:             string
  voertuigId:     string
  type:           VoertuigOnderhoudType
  uitvoerDatum:   string
  kilometerstand: number | null
  garage:         string | null
  beschrijving:   string | null
  /** Handmatig ingevoerde kosten (alleen relevant zonder factuur-koppeling). */
  kosten:         number | null
  createdAt:      string
  // FO-320 — factuur-koppeling. Hangt er een inkoopfactuurregel, dan is
  // `factuurRegelBedrag` de waarheid (de factuur wint bij een bedrag-conflict) en
  // is `handmatig` false. Zonder koppeling: `handmatig` true, `kosten` leidend.
  inkoopFactuurRegelId: string | null
  factuurId:            string | null
  factuurNr:            string | null
  factuurRegelBedrag:   number | null
  handmatig:            boolean
  // FO-329 (batch-38) — herkomst + factuur-koppelingen (bron/deel/credit) van
  // een automatisch geregistreerde regel. `koppelingenBedrag` = som van de
  // voertuig-aandelen over alle gekoppelde facturen (credit telt negatief);
  // dit is bij bron GARAGEFACTUUR het kostenbedrag (factuur = waarheid).
  // batch-59 voertuig-afspraken (FO-435 stap 1-2) — IMPORT = regel uit een door
  // de garage aangeleverde onderhoudshistorie (niet uit een factuur, niet met de
  // hand ingevoerd).
  bron:               'HANDMATIG' | 'GARAGEFACTUUR' | 'IMPORT'
  factuurKoppelingen: VoertuigOnderhoudFactuurKoppelingDTO[]
  koppelingenBedrag:  number | null
  // FO-329 — APK-vervaldatum-voorstel (bevestiging vereist, geen stille wijziging).
  apkVoorstelVervaldatum: string | null
  apkVoorstelStatus:      'OPEN' | 'BEVESTIGD' | 'AFGEWEZEN' | null
  // batch-62 garage-voertuig (FO-462 §A) - onderhoud vs schade/reparatie. Bij
  // factuurregels volgt de weergave uit de per-regel-indeling (gemengd = beide);
  // zonder factuurregels uit de eigen `kostenCategorie` (default via het type).
  kostenCategorie:         'ONDERHOUD' | 'SCHADE_REPARATIE' | null
  kostenCategorieWeergave: 'ONDERHOUD' | 'SCHADE_REPARATIE' | 'GEMENGD' | 'NOG_IN_TE_DELEN'
  // batch-62 (FO-465 §D) - velden die automatisch uit een factuur/document zijn
  // ingelezen ("uit factuur"); een handmatige correctie haalt ze eruit.
  uitFactuurVelden:        string[]
  /** De afspraak die op deze regel is afgerond (keten), of null. */
  afspraakId:              string | null
  /**
   * batch-65 apk-rapport (FO-478) - de APK-keuring uit een APK-rapport op deze
   * regel (uitslag, rapportnummer, vervaldatum volgens het rapport en het
   * rapport-document voor de same-origin download); null = geen APK-rapport.
   */
  apkRapport:              VoertuigOnderhoudApkRapportDTO | null
}

/** batch-65 apk-rapport (FO-478) - rapport-gegevens op een onderhoudslogregel. */
export interface VoertuigOnderhoudApkRapportDTO {
  uitslag:       ApkKeuringUitslag | null
  rapportnummer: string | null
  /** Nieuwe APK-vervaldatum volgens het rapport (yyyy-mm-dd), los van het voorstel. */
  vervaldatum:   string | null
  documentId:    string | null
  /** Laatste versie van het rapport-document; null = geen (leesbare) versie. */
  versionNr:     number | null
}

export interface VoertuigAuditDTO {
  id:           string
  voertuigId:   string
  actie:        VoertuigAuditActie
  actor:        string
  actorName:    string | null
  oudeWaarde:   unknown
  nieuweWaarde: unknown
  toelichting:  string | null
  createdAt:    string
}

/** FO-320 — een inkoopfactuurregel (voertuig-toewijzing) die nog aan een
 *  onderhoudslog gekoppeld kan worden (voor de onderhoud-modal). */
export interface KoppelbareFactuurRegelDTO {
  regelId: string
  factuurId: string
  factuurNr: string
  datum: string
  omschrijving: string
  /** Toegewezen voertuig-aandeel (excl. btw). */
  bedrag: number
}

// ─── FO-320 — voertuig-kostenbeeld (Kosten-tab) ──────────────────────────────
//
// Drie bronnen, elk met een eigen bron-badge zodat er geen dubbeltelling ontstaat:
// inkoop-toewijzingen (onderhoud/banden/…), tankpas-transacties (brandstof) en de
// vaste lasten (maandbedragen/lease/verzekering op het voertuig). Bedragen intern
// (kosten-gate). Doorklik naar de bron-factuur via `factuurId`.

export const VOERTUIG_KOSTEN_BRON = ['TOEWIJZING', 'TANKPAS', 'VASTE_LASTEN'] as const
export type VoertuigKostenBron = (typeof VOERTUIG_KOSTEN_BRON)[number]

export const VOERTUIG_KOSTEN_BRON_LABELS: Record<VoertuigKostenBron, string> = {
  TOEWIJZING:  'Inkoop-toewijzing',
  TANKPAS:     'Tankpas',
  VASTE_LASTEN: 'Vaste lasten',
}

/** Eén kostenpost in het voertuigbeeld (uit een van de drie bronnen). */
export interface VoertuigKostenPostDTO {
  id:           string
  /** Datum (yyyy-MM-dd, Europe/Amsterdam). */
  datum:        string
  bron:         VoertuigKostenBron
  bronLabel:    string
  /** Kostensoort-code uit de stamtabel (of afgeleid, bv. BRANDSTOF voor tankpas). */
  soort:        string
  soortLabel:   string
  omschrijving: string
  /** Bedrag (excl. btw). */
  bedrag:       number
  /** Doorklik-doel: de bron-factuur (alleen bij bron TOEWIJZING). */
  factuurId:    string | null
  factuurNr:    string | null
  // FO-339 — context-bestuurder destijds (wie reed op de kostendatum): TOEWIJZING
  // uit de context-koppeling, TANKPAS uit de transactie-bestuurder (FO-022).
  // Informatief; filterbaar op de voertuig-tab, telt niet mee in een totaal.
  contextEmployeeId?:   string | null
  contextEmployeeNaam?: string | null
}

export interface VoertuigKostenPeriodeDTO {
  /** 'yyyy' (jaar) of 'yyyy-MM' (maand), afhankelijk van de granulariteit. */
  periode:  string
  totaal:   number
  perSoort: Record<string, number>
}

/** TCO-samenvatting: kosten per maand + (indien km-standen) kosten per km. */
export interface VoertuigKostenTcoDTO {
  totaal:     number
  /** Aantal maanden in de periode (minimaal 1). */
  maanden:    number
  perMaand:   number
  /** Alleen wanneer er km-standen zijn: verreden km + kosten per km. */
  kmVerreden: number | null
  perKm:      number | null
}

export interface VoertuigKostenDTO {
  voertuigId: string
  kenteken:   string
  van:        string | null
  tot:        string | null
  soorten:    { soort: string; soortLabel: string }[]
  posten:     VoertuigKostenPostDTO[]
  /** Periode-buckets (maand of jaar) voor de totalen-weergave. */
  periodes:   VoertuigKostenPeriodeDTO[]
  totalen:    { totaal: number; perSoort: Record<string, number>; perBron: Record<string, number> }
  tco:        VoertuigKostenTcoDTO
}

/** Verkorte huidige toewijzing voor list-rij. */
export interface VoertuigCurrentToewijzingDTO {
  toewijzingId: string
  employeeId:   string
  employeeName: string
  startDatum:   string
  soort:        VoertuigToewijzingSoort
}

export interface VoertuigListRowDTO {
  id:                  string
  kenteken:            string
  type:                VoertuigType
  brandstof:           VoertuigBrandstof
  status:              VoertuigStatus
  eigendom:            VoertuigEigendom
  merk:                string
  model:               string
  bouwjaar:            number | null
  kilometerstand:      number | null
  apkVervaldatum:      string | null
  apkDagenTotVerloop:  number | null
  /** Standaard-bijtelling van het voertuig — voorvulwaarde voor de
   *  onboarding-bijtelling-keuze (kan per medewerker afwijken). */
  bijtelling:          boolean | null
  /** Aantal openstaande RDW-terugroepacties (recalls) — voedt de lijst-badge. */
  aantalOpenstaandeTerugroepacties: number
  huidigeToewijzing:   VoertuigCurrentToewijzingDTO | null
  /** batch-37 (FO-327) — actuele onderhoud-verwachting (nieuwste rapport wint); null = nooit gemeld. */
  onderhoudVerwachting: OnderhoudVerwachtingSamenvattingDTO | null
  /** batch-38 (FO-330) — verzekeraarsgegevens voor de schade-flow incompleet (lijst-badge + filter). */
  verzekeringIncompleet: boolean
  /**
   * batch-59 voertuig-afspraken (FO-435 stap 4) — "Actie vereist" / "Gepland
   * {datum}" / "Uitgevoerd" per voertuig, SERVER-SIDE gerekend (nooit in de
   * client, anders lopen lijst en KPI-teller uiteen).
   */
  actieStatus: VoertuigActieStatusDTO
  /**
   * batch-68 werkplaatsfacturen (FO-341 §3.4) - berekende onderhoudsstatus (uit
   * de reminders: volgende beurt in datum en km) + APK voorlopig. Optioneel:
   * null = geen beurt-historie bekend.
   */
  onderhoudBadge?: VoertuigOnderhoudLijstBadgeDTO | null
  createdAt:           string
}

/**
 * batch-38 schade-pdf (FO-330) — definitie "verzekering incompleet": de
 * kern-velden die de schade-flow nodig heeft (vak 8 maatschappij + polisnr.
 * en de conceptmail-ontvanger) ontbreken. Aanhangers (geen eigen polis; WA
 * loopt via het trekkend voertuig) en uitgefaseerde voertuigen tellen niet
 * mee — het filter is een aanvul-werkvoorraad, geen archiefcontrole.
 * Gedeeld door de API-lijstfilter/badge en tests.
 */
export function isVerzekeringIncompleet(v: {
  type: VoertuigType
  status: VoertuigStatus
  verzekeraar: string | null
  verzekeringPolisNummer: string | null
  verzekeraarEmail: string | null
}): boolean {
  if (v.type === 'AANHANGER') return false
  if (v.status === 'VERKOCHT' || v.status === 'AFGESCHREVEN') return false
  return !v.verzekeraar?.trim() || !v.verzekeringPolisNummer?.trim() || !v.verzekeraarEmail?.trim()
}

export interface VoertuigDetailDTO {
  id:        string
  kenteken:  string
  type:      VoertuigType
  brandstof: VoertuigBrandstof
  status:    VoertuigStatus
  eigendom:  VoertuigEigendom

  merk:           string
  model:          string
  bouwjaar:       number | null
  kleur:          string | null
  chassisNummer:  string | null
  transmissie:    string | null
  kilometerstand: number | null

  apkVervaldatum:         string | null
  verzekeringVervaldatum: string | null
  verzekeraar:            string | null
  verzekeringPolisNummer: string | null
  // batch-37/38 schadeformulier (FO-326/FO-330) — sectie Verzekering.
  groeneKaartNummer:      string | null
  groeneKaartGeldigVanaf: string | null
  groeneKaartGeldigTot:   string | null
  verzekeringAgentschap:  string | null
  verzekeringAllRisks:    boolean | null
  verzekeraarEmail:       string | null
  rechtsbijstandVerzekering:  boolean | null
  rechtsbijstandPolisNummer:  string | null
  rechtsbijstandMaatschappij: string | null
  inzittendenVerzekering:     boolean | null
  inzittendenPolisNummer:     string | null
  inzittendenMaatschappij:    string | null
  verzekeringspakket:     string | null

  laatsteOnderhoudDatum:   string | null
  volgendeOnderhoudKm:     number | null
  garageNaam:              string | null
  onderhoudBij:            string | null
  locatieGarage:           string | null
  laatsteOnderhoudsmoment: string | null
  onderhoudshistorie:      string | null

  leaseMaatschappij:  string | null
  contractNummer:     string | null
  contractStartDatum: string | null
  contractEindDatum:  string | null
  looptijdMaanden:    number | null
  maandbedragExclBtw: number | null
  maandbedragInclBtw: number | null

  manierVanAankoop: string | null
  gekochtBijGarage: string | null

  tankpasNummer: string | null
  /** FO-444 (spec 144) - NMBRS-voertuigreferentie zoals op de loonstrook. */
  nmbrsReferentie: string | null

  aanschafDatum:   string | null
  aanschafBedrag:  number | null
  cataloguswaarde: number | null
  /** FO-361 (batch-41) - dagwaarde (waarde economisch verkeer, youngtimer-grondslag). */
  dagwaarde: number | null

  // Spec 40 - bijtelling afgeleid van de auto: datum eerste toelating (input) +
  // de gecachte berekende regeling (herberekend bij input-/tabelwijziging) +
  // de toegepaste staffel (laag%/cap/hoog%) voor de weergave.
  datumEersteToelating:  string | null
  bijtellingRegelingId:  string | null
  bijtellingJaarbedrag:  number | null
  bijtellingMaandbedrag: number | null
  bijtellingVervaltOp:   string | null
  bijtellingBerekendOp:  string | null
  /** De toegepaste tariefstaffel (laag% tot cap, hoog% daarboven); null = geen regeling/onvolledig. */
  bijtellingStaffel: {
    laagPercentage: number
    capBedrag:      number | null
    hoogPercentage: number
  } | null

  // FO-038 (batch-16, punt 3) — bijtelling is één bron op de medewerker. Deze
  // velden zijn de LEGACY voertuig-kolommen (read-only, niet meer bewerkbaar);
  // de UI toont `bestuurderBijtelling` (afgeleid van de huidige bestuurder).
  bijtelling:           boolean | null
  bijtellingPercentage: number | null
  bijtellingIndicatie:  string | null
  /** Bijtelling van de huidige bestuurder (Employee.bijtelling). null = geen bestuurder / onbekend. */
  bestuurderBijtelling:           boolean | null
  /** Bijtelling-percentage van de huidige bestuurder. null = onbekend. */
  bestuurderBijtellingPercentage: number | null
  kentekenKleur:        string | null

  // Borg
  borgBetaald: boolean | null
  borgBedrag:  number | null

  // Documenten
  kentekenBewijsAanwezig:   boolean | null
  aankoopBewijsAanwezig:    boolean | null
  schemaEnContractAanwezig: boolean | null

  notities: string | null

  apkDagenTotVerloop:         number | null
  verzekeringDagenTotVerloop: number | null

  huidigeToewijzing: VoertuigCurrentToewijzingDTO | null

  // FO-040 (batch-16) — dubbele-bestuurder-waarschuwing (afgeleid van de actieve
  // toewijzingen). `overlapWaarschuwing` = null als er geen overlap is.
  overlapWaarschuwing: string | null
  overlapRedenen:      ToewijzingOverlapReden[]

  // RDW-synchronisatie — gevuld door dagelijkse cron + handmatige resync. Geen
  // veld-mutatie-audit op deze drie velden zelf; wijzigingen aan inhoudelijke
  // velden (merk/model/apk/...) staan in de VoertuigAudit-tab.
  rdwLaatsteSyncAt:     string | null
  rdwLaatsteSyncStatus: string | null
  rdwLaatsteSyncError:  string | null

  // batch-46 (FO-394) — herkomst per veld: welke velden nog RDW-beheerd zijn
  // (de sync houdt ze bij, handmatige edit snijdt eruit) + wanneer RDW-data voor
  // het laatst is toegepast. Voedt de "Opgehaald bij RDW"-badge in de detail-UI.
  bronRdw:            string[]
  rdwLaatstOpgehaald: string | null

  /** Openstaande + (recent) afgehandelde RDW-terugroepacties, nieuwste eerst.
   *  Openstaande staan vooraan; afgehandelde (soft-deleted) komen niet mee. */
  terugroepacties: VoertuigTerugroepactieDTO[]

  /**
   * batch-59 voertuig-afspraken (FO-436) — dezelfde recalls MET de interne
   * afhandel-registratie en de garagefactuur-hint. Het dossier toont beide
   * waarheden naast elkaar ("RDW: openstaand - intern: uitgevoerd op {datum}");
   * `terugroepacties` hierboven blijft de rauwe RDW-spiegel voor bestaande
   * consumers (portal-kaart).
   */
  terugroepDossier: VoertuigTerugroepDossierDTO[]

  /** batch-59 (FO-435) — de afspraken van dit voertuig, nieuwste eerst. */
  afspraken: VoertuigAfspraakDTO[]

  /** batch-59 (FO-435 stap 4) — server-side gerekende actie-status. */
  actieStatus: VoertuigActieStatusDTO

  /** batch-37 (FO-327) — actuele onderhoud-verwachting (nieuwste rapport wint); null = nooit gemeld. */
  onderhoudVerwachting: OnderhoudVerwachtingSamenvattingDTO | null

  /**
   * batch-65 apk-rapport (FO-478) — de vastgelegde APK-rapporten van dit
   * voertuig, nieuwste keuring eerst (APK-kaart "laatste APK-rapport" +
   * blok op de Documenten-tab). Per voertuig begrensd (circa één per jaar).
   */
  apkRapporten: VoertuigApkRapportDTO[]

  /** batch-70 voertuig-meldingen (FO-527 §B4) - de gekoppelde leasemaatschappij (relatie). */
  leaseMaatschappijId?: string | null
  leaseMaatschappijNaam?: string | null

  createdAt: string
  updatedAt: string
}

export interface VoertuigHistorieDTO {
  toewijzingen: VoertuigToewijzingDTO[]
  onderhouden:  VoertuigOnderhoudDTO[]
  audits:       VoertuigAuditDTO[]
}

export interface VoertuigStatsDTO {
  totaal:           number
  actief:           number
  onderhoud:        number
  beschikbaar:      number
  /** FO-038 (batch-16) — defect/niet-inzetbaar (los van onderhoud). */
  kapot:            number
  apkBinnen30Dagen: number
  /**
   * batch-59 voertuig-afspraken (FO-435 stap 4 + FO-436 §5) — werkvoorraad.
   * `actieVereist`: voertuigen met een vervalmoment/recall binnen de drempel
   * ZONDER afspraak. `afsprakenGepland`: voertuigen waar alles wat speelt
   * gepland staat. `openRecallsIntern`: voertuigen met een terugroepactie die
   * INTERN nog open is (een door ons uitgevoerde recall telt niet mee, ook al
   * blijft RDW "O" publiceren).
   */
  actieVereist:      number
  afsprakenGepland:  number
  openRecallsIntern: number
}

/**
 * Fabrikant-pechhulp-info — server-side opgehaald als voertuig ≤ 2 jaar oud is.
 * Gebruikt door de "Pech onderweg"-info-card op portal /mijn-voertuig.
 */
export interface MerkNoodnummerDTO {
  /** Weergavenaam, bv. "Peugeot Assistance". */
  label:       string
  /** Nederlands nummer (klikbare tel: link in UI). */
  telefoonNL:  string
  /** Belgisch/buitenland-nummer; null als merk geen buitenland-lijn heeft. */
  telefoonBE:  string | null
  /** Extra context, bv. "Via Stellantis". null als niet relevant. */
  notitie:     string | null
}

/** Portal-DTO voor /mijn-voertuig. */
export interface MijnVoertuigDTO {
  id:                  string
  kenteken:            string
  type:                VoertuigType
  brandstof:           VoertuigBrandstof
  merk:                string
  model:               string
  bouwjaar:            number | null
  apkVervaldatum:      string | null
  apkDagenTotVerloop:  number | null
  toegewezenSinds:     string

  /** Openstaande RDW-terugroepacties (recalls) op dit voertuig — getoond als
   *  waarschuwingskaart in de portal. Leeg = geen openstaande actie. */
  openstaandeTerugroepacties: VoertuigTerugroepactieDTO[]

  // ── FO-113 (batch-22) — verklaring zakelijk/privé-gebruik ─────────
  /** De actieve toewijzing van deze medewerker (voor de verklaring-actie). */
  toewijzingId:           string
  soort:                  VoertuigToewijzingSoort
  /** Of deze toewijzing standaard een verklaring vraagt (VAST = ja). */
  vereistVerklaring:      boolean
  /** Server-derived status zakelijk/privé op de actieve toewijzing. */
  verklaringStatus:       VoertuigVerklaringStatus
  /** Type van de actieve geldige verklaring (null als geen geldige). */
  verklaringType:         VoertuigGebruikType | null
  /** Datum (ISO) van de actieve geldige verklaring, of null. */
  verklaringVerklaardOp:  string | null
  laatsteOnderhouden:  Array<{
    id:           string
    type:         VoertuigOnderhoudType
    uitvoerDatum: string
    beschrijving: string | null
  }>

  // ── Onderhoud/schade info-cards (BLOK 2 ronde 2) ─────────────────
  /** Berekend: huidige jaar - bouwjaar. null als bouwjaar onbekend. */
  voertuigLeeftijd:       number | null
  /** True als gekochtBijGarage of leaseMaatschappij "Wassink" bevat (case-insensitive). */
  isWassinkVoertuig:      boolean
  /** Vrij veld op Voertuig — bron-garage waar het voertuig is gekocht. */
  gekochtBijGarage:       string | null
  /** Fabrikant-noodnummer-info, alleen gevuld als voertuig ≤ 2 jaar oud is en merk in tabel staat. */
  merkNoodnummer:         MerkNoodnummerDTO | null

  // ── FO-157 (batch-31) — kilometerstand-invoer ─────────────────────
  /** Huidige kilometerstand-cache op het voertuig (laatst bekende waarde). */
  kilometerstand:         number | null
  /** Laatste km-registratie (historie) — referentie voor de invoer; null = nog nooit doorgegeven. */
  laatsteKmRegistratie:   { kmStand: number; datum: string } | null

  // ── batch-37 (FO-327) — onderhoud-verwachting uit het telematica-rapport ──
  /** Actuele onderhoud-verwachting (nieuwste rapport wint); null = nooit gemeld. */
  onderhoudVerwachting:   OnderhoudVerwachtingSamenvattingDTO | null

  // ── batch-68 werkplaatsfacturen (FO-341 §3.4) — badge onderhoud + APK ──
  /** Laatste/volgende beurt en APK (met RDW-status); zonder bedragen. */
  onderhoudBadge:         VoertuigOnderhoudBadgeDTO | null
}

/**
 * Portal-DTO voor /voertuigen/me/historie — lichtgewicht item per toewijzing
 * voor de historie-lijst in /mijn-gegevens?tab=voertuig. Volledige voertuig-
 * details (APK, schade-info, onderhoud) staan in MijnVoertuigDTO op
 * /voertuigen/me.
 */
export interface MijnVoertuigHistorieItemDTO {
  toewijzingId: string
  voertuigId:   string
  kenteken:     string
  merk:         string
  model:        string
  type:         VoertuigType
  brandstof:    VoertuigBrandstof
  bouwjaar:     number | null
  /** ISO datetime van toewijzing-startDatum. */
  startDatum:   string
  /** ISO datetime van toewijzing-eindDatum, of null = open toewijzing. */
  eindDatum:    string | null
  /**
   * Server-derived: `toewijzing.eindDatum === null && voertuig.deletedAt === null`.
   * Een open toewijzing op een soft-deleted voertuig is GEEN huidig voertuig.
   */
  isHuidig:     boolean
}

/**
 * Document gekoppeld aan een voertuig (via DocumentKoppeling entiteitType=VOERTUIG).
 * Voor het Documenten-blok op de voertuig-detailpagina. Toont de laatste versie
 * met een download-link (`/api/documenten/:id/versies/:nr/download`).
 */
export interface VoertuigDocumentDTO {
  documentId:     string
  titel:          string
  categorie:      string
  /** Laatste versienummer (voor de download-URL). */
  versionNr:      number
  fileName:       string
  fileSize:       number
  mimeType:       string
  /** ISO datetime van upload van de laatste versie. */
  uploadedAt:     string
  uploadedByName: string | null
  /**
   * batch-65 apk-rapport (FO-478) - alleen bij het (laatste) APK-rapport in de
   * portal: de keuringsdatum (yyyy-mm-dd) voor de regel "gekeurd op ...".
   */
  apkKeuringsdatum?: string | null
}
